import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Header from "../components/Common/Header";
import {
  MemoryUpdate,
  getMemory,
  updateMemory,
  deleteMemory,
  untagItemFromMemory,
  tagItemToMemory,
} from "../api/memories";
import { LocationPicker } from "../components/Location";
import { PersonPicker } from "../components/Person";
import { useMediaUrl } from "../hooks/useMediaUrl";

// Component to display a media thumbnail with auth
function MediaThumbnail({ mediaId, filename }: { mediaId: number; filename: string }) {
  const { url, isLoading } = useMediaUrl(mediaId);
  
  if (isLoading) {
    return (
      <div className="h-20 w-20 bg-vault-100 rounded animate-pulse" />
    );
  }
  
  return (
    <img
      src={url || ""}
      alt={filename}
      className="h-20 w-20 object-cover rounded"
    />
  );
}

export default function MemoryDetail() {
  const { memoryId } = useParams<{ memoryId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editData, setEditData] = useState<MemoryUpdate>({});
  const [showAddPeople, setShowAddPeople] = useState(false);

  // Fetch memory details
  const { data: memory, isLoading, error } = useQuery({
    queryKey: ["memory", memoryId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getMemory(token, parseInt(memoryId!));
    },
    enabled: !!memoryId,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: MemoryUpdate) => {
      const token = await getAccessTokenSilently();
      return updateMemory(token, parseInt(memoryId!), data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memory", memoryId] });
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setIsEditing(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return deleteMemory(token, parseInt(memoryId!));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      navigate("/memories");
    },
  });

  // Untag mutation
  const untagMutation = useMutation({
    mutationFn: async (params: { mediaId?: number; personId?: number; connectedPersonId?: number }) => {
      const token = await getAccessTokenSilently();
      return untagItemFromMemory(token, parseInt(memoryId!), {
        media_id: params.mediaId,
        person_id: params.personId,
        connected_person_id: params.connectedPersonId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memory", memoryId] });
    },
  });

  // Tag mutation (for adding people)
  const tagMutation = useMutation({
    mutationFn: async (params: { personId?: number; connectedPersonId?: number }) => {
      const token = await getAccessTokenSilently();
      return tagItemToMemory(token, parseInt(memoryId!), {
        person_id: params.personId,
        connected_person_id: params.connectedPersonId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memory", memoryId] });
    },
  });

  const startEditing = () => {
    if (memory) {
      setEditData({
        title: memory.title,
        description: memory.description || "",
        memory_date: memory.memory_date || "",
        location_id: memory.location?.id || undefined,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editData);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8">
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-vault-300 border-t-vault-600" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !memory) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8">
          <div className="rounded-lg bg-red-50 p-4 text-red-700">
            Failed to load memory. It may have been deleted.
          </div>
          <Link to="/memories" className="mt-4 inline-block text-vault-600 hover:underline">
            ← Back to Memories
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <Link to="/memories" className="text-sm text-vault-600 hover:text-vault-900">
            ← Back to Memories
          </Link>
        </nav>

        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-purple-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <div>
              <h1 className="font-serif text-3xl font-bold text-vault-900">{memory.title}</h1>
              <div className="flex gap-2 text-vault-600">
                {memory.memory_date && <span>{memory.memory_date}</span>}
                {memory.location && (
                  <>
                    {memory.memory_date && <span>·</span>}
                    <Link to={`/locations/${memory.location.id}`} className="hover:underline">
                      {memory.location.name}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={startEditing}
              className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded-lg bg-red-100 px-4 py-2 text-red-700 hover:bg-red-200"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Description */}
        {memory.description && (
          <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
            <p className="text-vault-700">{memory.description}</p>
          </div>
        )}

        {/* Tagged Media */}
        <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-vault-900">
            Photos ({memory.media_items.length})
          </h2>
          
          {memory.media_items.length === 0 ? (
            <p className="text-vault-500">No photos in this memory yet.</p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
              {memory.media_items.map((media) => (
                <div key={media.id} className="group relative">
                  <MediaThumbnail mediaId={media.id} filename={media.filename} />
                  <button
                    onClick={() => untagMutation.mutate({ mediaId: media.id })}
                    className="absolute -top-1 -right-1 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-xs"
                    title="Remove from memory"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tagged People */}
        <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-vault-900">
              People ({memory.persons.length + memory.connected_persons.length})
            </h2>
            <button
              onClick={() => setShowAddPeople(!showAddPeople)}
              className="text-sm text-vault-600 hover:text-vault-900 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add People
            </button>
          </div>

          {/* Add People Picker */}
          {showAddPeople && (
            <div className="mb-4 p-4 bg-vault-50 rounded-lg">
              <PersonPicker
                selectedPersonIds={memory.persons.map((p) => p.id)}
                selectedConnectedPersonIds={memory.connected_persons.map((p) => p.id)}
                onAddPerson={(id) => tagMutation.mutate({ personId: id })}
                onAddConnectedPerson={(id) => tagMutation.mutate({ connectedPersonId: id })}
                onRemovePerson={(id) => untagMutation.mutate({ personId: id })}
                onRemoveConnectedPerson={(id) => untagMutation.mutate({ connectedPersonId: id })}
                placeholder="Search for people to add..."
              />
              <button
                onClick={() => setShowAddPeople(false)}
                className="mt-2 text-sm text-vault-600 hover:text-vault-900"
              >
                Done adding people
              </button>
            </div>
          )}
          
          {memory.persons.length === 0 && memory.connected_persons.length === 0 ? (
            <p className="text-vault-500">No people tagged in this memory yet.</p>
          ) : (
            <div className="space-y-2">
              {memory.persons.map((person) => (
                <div key={`person-${person.id}`} className="flex items-center justify-between py-2 border-b border-vault-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-vault-900">{person.display_name}</span>
                    <span className="text-xs text-vault-500 bg-vault-100 px-2 py-0.5 rounded">Family</span>
                  </div>
                  <button
                    onClick={() => untagMutation.mutate({ personId: person.id })}
                    className="text-vault-400 hover:text-red-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              {memory.connected_persons.map((person) => (
                <div key={`cp-${person.id}`} className="flex items-center justify-between py-2 border-b border-vault-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-vault-900">{person.display_name}</span>
                    <span className="text-xs text-vault-500 bg-amber-100 px-2 py-0.5 rounded">Connected</span>
                  </div>
                  <button
                    onClick={() => untagMutation.mutate({ connectedPersonId: person.id })}
                    className="text-vault-400 hover:text-red-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-xl font-semibold text-vault-900">Edit Memory</h2>

            {updateMutation.error && (
              <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">
                {updateMutation.error instanceof Error ? updateMutation.error.message : "Failed to update"}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-vault-700">Title</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={editData.title || ""}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">Description</label>
                <textarea
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  rows={3}
                  value={editData.description || ""}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">Date</label>
                <input
                  type="text"
                  placeholder='e.g., "25 December 1965"'
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={editData.memory_date || ""}
                  onChange={(e) => setEditData({ ...editData, memory_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">Location</label>
                <LocationPicker
                  value={editData.location_id || null}
                  onChange={(id) => setEditData({ ...editData, location_id: id })}
                  placeholder="Where did this happen?"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsEditing(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-vault-900">Delete Memory?</h2>
            <p className="mb-4 text-vault-600">
              Are you sure you want to delete <span className="font-medium">{memory.title}</span>?
              This won&apos;t delete the photos or people, just the memory grouping.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
